/**
 * DeviceList Component
 * Lista virtualizada de dispositivos com otimizações de performance
 * Suporta atualizações em tempo real via WebSocket
 *
 * 2ECOMP: Otimizado para 3000+ dispositivos
 * 2ECOMP Fase 3: Keyboard navigation e acessibilidade
 */

import {
 useRef, useMemo, useCallback, useEffect, memo, useState
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { makeStyles } from 'tss-react/mui';
import { Box, Typography } from '@mui/material';
import { DirectionsCar, SearchOff } from '@mui/icons-material';
import DeviceRow from './DeviceRow';
import { devicesActions } from '../store';
import { useTranslation } from '../common/components/LocalizationProvider';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

// 2ECOMP: Alturas FIXAS - margins padronizadas no DeviceRow
// Normal/Selected: margin(4+4) + padding(10+10) + content(~38) + border(2) = ~68px
// Expanded: normal(68) + marginTop(6) + paddingTop(6) + border(1) + TelemetryBar(32) + gap(6) + QuickActions(48) = ~167px
const DEVICE_ROW_HEIGHT = 68;
const DEVICE_ROW_HEIGHT_EXPANDED = 168;

// 2ECOMP Fase 6: Componente memoizado para cada row virtual
// Evita re-render quando apenas o style.transform muda
// 2ECOMP Fase 3: Adicionado suporte a ARIA e ref para scrollIntoView
const VirtualRow = memo(({
  device, position, style, className, expandedId, onToggleExpand,
  isSelected, rowRef, index, totalCount,
}) => {
  const isExpanded = expandedId === device?.id;
  return (
    <div
      ref={isSelected ? rowRef : null}
      id={`device-row-${device?.id}`}
      className={className}
      style={style}
      role="option"
      aria-selected={isSelected}
      aria-expanded={isExpanded}
      aria-setsize={totalCount}
      aria-posinset={index + 1}
      data-device-id={device?.id}
      tabIndex={isSelected ? 0 : -1}
    >
      <DeviceRow
        device={device}
        position={position}
        expandedId={expandedId}
        onToggleExpand={onToggleExpand}
        isSelected={isSelected}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // 2ECOMP Fase 6: Comparação otimizada - delega comparação de position para DeviceRow
  // Só verifica se as referências são as mesmas ou se expandedId mudou para este device
  if (prevProps.device !== nextProps.device) return false;

  // 2ECOMP Fase 3: Re-render se seleção mudou
  if (prevProps.isSelected !== nextProps.isSelected) return false;

  // Se expandedId mudou para/de este device, precisa re-renderizar
  const deviceId = prevProps.device?.id;
  const wasExpanded = prevProps.expandedId === deviceId;
  const isExpanded = nextProps.expandedId === deviceId;
  if (wasExpanded !== isExpanded) return false;

  // Se style.transform mudou (scroll), mas position e device são iguais, não precisa re-renderizar
  // porque o DeviceRow tem seu próprio comparador
  return (
    prevProps.position === nextProps.position &&
    prevProps.style.transform === nextProps.style.transform
  );
});

const useStyles = makeStyles()((theme, { tokens }) => ({
  container: {
    flex: 1,
    width: '100%',
    overflow: 'auto',
    // 2ECOMP: Removido scrollBehavior smooth - conflita com virtualização
    // Otimização de scroll via GPU
    willChange: 'transform',
    minHeight: 0, // Important for flex children
    // Otimização para scroll suave (iOS)
    WebkitOverflowScrolling: 'touch',
  },
  // 2ECOMP Fase 7: Empty state refinado com ícone e animação
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(4),
    textAlign: 'center',
    animation: '$fadeIn 0.3s ease-out',
  },
  emptyIcon: {
    fontSize: 64,
    color: tokens?.text?.muted || theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
    opacity: 0.5,
  },
  emptyTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: tokens?.text?.secondary || theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  emptySubtitle: {
    fontSize: '0.85rem',
    color: tokens?.text?.muted || theme.palette.text.disabled,
    maxWidth: 280,
  },
  '@keyframes fadeIn': {
    '0%': { opacity: 0, transform: 'translateY(10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
  virtualRow: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    // 2ECOMP Fase 6: GPU acceleration + containment para cada row
    willChange: 'transform',
    contain: 'layout style',
    // 2ECOMP Fase 3: Focus visible para navegação por teclado
    '&:focus-visible': {
      outline: `2px solid ${tokens?.accent?.primary || theme.palette.primary.main}`,
      outlineOffset: -2,
      zIndex: 10,
    },
  },
  // 2ECOMP Fase 3: Container focável para keyboard navigation
  // Altura definida via inline style (rowVirtualizer.getTotalSize())
  listContainer: {
    width: '100%',
    position: 'relative',
    '&:focus': {
      outline: 'none',
    },
  },
}));

const DeviceList = ({ devices = [], hasActiveFilter = false }) => {
  const tokens = usePremiumTokens();
  const { classes } = useStyles({ tokens });
  const containerRef = useRef(null);
  const selectedRowRef = useRef(null);
  const t = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 2ECOMP: Estado para controlar qual row está expandida
  const [expandedId, setExpandedId] = useState(null);

  // 2ECOMP Fase 3: Device selecionado do Redux
  const selectedId = useSelector((state) => state.devices.selectedId);

  // Posições dos dispositivos do Redux (atualizam em tempo real)
  // 2ECOMP: shallowEqual NÃO bloqueia atualizações
  const positions = useSelector((state) => state.session.positions, shallowEqual);

  // 2ECOMP: Sincronizar expandedId com selectedId
  // Quando seleção muda (de qualquer lugar: mapa, painel, etc.), colapsa card anterior
  // Isso evita ter dois cards destacados simultaneamente
  useEffect(() => {
    if (selectedId && expandedId && selectedId !== expandedId) {
      // Usuário selecionou outro device - colapsar o expandido
      setExpandedId(null);
    }
  }, [selectedId]); // Não incluir expandedId para evitar loop

  // Memoizar a lista de dispositivos
  const safeDevices = useMemo(
    () => (Array.isArray(devices) ? devices : []),
    [devices]
  );

  // 2ECOMP Fase 3: Índice do device selecionado na lista atual
  const selectedIndex = useMemo(() => {
    if (!selectedId) return -1;
    return safeDevices.findIndex((d) => d.id === selectedId);
  }, [selectedId, safeDevices]);

  // 2ECOMP: Handler para toggle expand - memoizado
  const handleToggleExpand = useCallback((deviceId) => {
    setExpandedId(deviceId);
  }, []);

  // 2ECOMP Fase 3: Handler para selecionar device por índice
  const selectDeviceByIndex = useCallback((index) => {
    if (index >= 0 && index < safeDevices.length) {
      const device = safeDevices[index];
      dispatch(devicesActions.selectId(device.id));
    }
  }, [safeDevices, dispatch]);

  // 2ECOMP Fase 3: Keyboard navigation handler
  const handleKeyDown = useCallback((e) => {
    // Ignorar se estiver em input de busca ou outro campo
    const tagName = e.target.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea' || e.target.isContentEditable) {
      return;
    }

    // Ignorar se não houver devices
    if (safeDevices.length === 0) return;

    const currentIndex = selectedIndex >= 0 ? selectedIndex : -1;

    switch (e.key) {
      // Navegação na lista
      case 'ArrowDown':
        e.preventDefault();
        if (currentIndex < safeDevices.length - 1) {
          selectDeviceByIndex(currentIndex + 1);
        } else if (currentIndex === -1 && safeDevices.length > 0) {
          selectDeviceByIndex(0);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (currentIndex > 0) {
          selectDeviceByIndex(currentIndex - 1);
        } else if (currentIndex === -1 && safeDevices.length > 0) {
          selectDeviceByIndex(safeDevices.length - 1);
        }
        break;

      case 'Home':
        e.preventDefault();
        selectDeviceByIndex(0);
        break;

      case 'End':
        e.preventDefault();
        selectDeviceByIndex(safeDevices.length - 1);
        break;

      // Ações no device selecionado
      case 'Enter':
      case ' ': // Space
        e.preventDefault();
        if (selectedId) {
          setExpandedId((prev) => (prev === selectedId ? null : selectedId));
        }
        break;

      case 'Escape':
        e.preventDefault();
        if (expandedId) {
          setExpandedId(null);
        }
        break;

      // Atalhos rápidos (só funcionam se houver device selecionado)
      case 'm':
      case 'M':
        // M = Focar no mapa (já está selecionado, só precisa garantir que o mapa centralize)
        if (selectedId && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          // Re-selecionar para trigger o mapa centralizar
          dispatch(devicesActions.selectId(selectedId));
        }
        break;

      case 'h':
      case 'H':
        // H = Histórico/Replay
        if (selectedId && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          navigate(`/replay?deviceId=${selectedId}`);
        }
        break;

      case 'e':
      case 'E':
        // E = Editar device
        if (selectedId && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          navigate(`/settings/device/${selectedId}`);
        }
        break;

      default:
        break;
    }
  }, [safeDevices, selectedIndex, selectedId, expandedId, selectDeviceByIndex, dispatch, navigate]);

  // 2ECOMP Fase 3: Attach keyboard listener
  useEffect(() => {
    const handleGlobalKeyDown = (e) => handleKeyDown(e);
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  // 2ECOMP: Função para calcular altura da row (dinâmica para expandido)
  const getRowHeight = useCallback((index) => {
    const device = safeDevices[index];
    if (device && device.id === expandedId) {
      return DEVICE_ROW_HEIGHT_EXPANDED;
    }
    return DEVICE_ROW_HEIGHT;
  }, [safeDevices, expandedId]);

  // 2ECOMP: Configuração otimizada do virtualizador
  // - overscan alto (15) para evitar áreas em branco durante scroll rápido
  // - SEM startTransition no onChange (causa blank areas)
  const rowVirtualizer = useVirtualizer({
    count: safeDevices.length,
    getScrollElement: () => containerRef.current,
    estimateSize: getRowHeight,
    // 2ECOMP: overscan alto para scroll suave - renderiza 15 itens extras
    overscan: 15,
    // Chave única para cada item (importante para atualizações em tempo real)
    getItemKey: useCallback((index) => safeDevices[index]?.id ?? index, [safeDevices]),
  });

  // 2ECOMP: Re-measure FORÇANDO recálculo de todos os índices quando expandedId muda
  // Isso corrige o problema de sobreposição (accordion)
  useEffect(() => {
    // Força recálculo de todas as alturas
    rowVirtualizer.measure();
    // Pequeno delay para garantir que o DOM atualizou
    const timer = setTimeout(() => {
      rowVirtualizer.measure();
    }, 50);
    return () => clearTimeout(timer);
  }, [expandedId]);

  // 2ECOMP Fase 3: Scroll to selected item quando seleção muda via keyboard
  const prevSelectedIndexRef = useRef(selectedIndex);
  useEffect(() => {
    // Só faz scroll se o índice mudou (navegação por teclado)
    if (selectedIndex >= 0 && selectedIndex !== prevSelectedIndexRef.current) {
      rowVirtualizer.scrollToIndex(selectedIndex, {
        align: 'auto', // 'auto' mantém visível sem centralizar desnecessariamente
        behavior: 'smooth',
      });
    }
    prevSelectedIndexRef.current = selectedIndex;
  }, [selectedIndex, rowVirtualizer]);

  // Track device count para detectar mudanças de filtro
  const prevCountRef = useRef(0);

  // Scroll para o topo apenas quando a quantidade de devices mudar (filtro)
  useEffect(() => {
    if (prevCountRef.current > 0 && prevCountRef.current !== safeDevices.length) {
      // Quantidade mudou - usuário aplicou/removeu filtro
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }
    prevCountRef.current = safeDevices.length;
  }, [safeDevices.length]);

  // 2ECOMP Fase 7: Estado vazio com ícone e mensagens diferenciadas
  if (safeDevices.length === 0) {
    return (
      <Box className={classes.emptyState}>
        {hasActiveFilter ? (
          // Estado: Filtro ativo mas sem resultados
          <>
            <SearchOff className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              {t('sharedNoResults') || 'Nenhum resultado encontrado'}
            </Typography>
            <Typography className={classes.emptySubtitle}>
              {t('sharedNoResultsHint') || 'Tente ajustar os filtros ou busca para encontrar dispositivos'}
            </Typography>
          </>
        ) : (
          // Estado: Lista vazia (sem dispositivos)
          <>
            <DirectionsCar className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              {t('sharedNoDevices') || 'Nenhum dispositivo'}
            </Typography>
            <Typography className={classes.emptySubtitle}>
              {t('sharedNoDevicesHint') || 'Adicione dispositivos para começar a rastrear sua frota'}
            </Typography>
          </>
        )}
      </Box>
    );
  }

  // 2ECOMP: Usar getVirtualItems() diretamente - mais performático que estado
  const virtualItems = rowVirtualizer.getVirtualItems();

  return (
    <div
      ref={containerRef}
      className={classes.container}
      role="listbox"
      aria-label={t('deviceTitle') || 'Lista de dispositivos'}
      aria-activedescendant={selectedId ? `device-row-${selectedId}` : undefined}
      tabIndex={0}
    >
      {/* Container com altura total para scroll correto */}
      <div
        className={classes.listContainer}
        style={{
          height: rowVirtualizer.getTotalSize(),
        }}
      >
        {virtualItems.map((virtualRow) => {
          const device = safeDevices[virtualRow.index];
          if (!device) return null;
          const isDeviceSelected = device.id === selectedId;

          return (
            <VirtualRow
              key={device.id}
              device={device}
              position={positions[device.id]}
              expandedId={expandedId}
              onToggleExpand={handleToggleExpand}
              isSelected={isDeviceSelected}
              rowRef={selectedRowRef}
              index={virtualRow.index}
              totalCount={safeDevices.length}
              className={classes.virtualRow}
              style={{
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export default DeviceList;
